import { CheckCircleRounded } from '@mui/icons-material'
import { Box, FormHelperText, Grid, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdArrowBack, MdArrowForward, MdDeleteOutline } from 'react-icons/md'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { FlexSpinner } from 'src/components/Spinner'
import { APIResultCount } from 'src/constant'
import { z } from 'zod'
import { useGetDropDownPositionDrawings, useGetPositionDrawingsForMooring } from '../../../../api/position-drawing'
import { AppForm, AppFormProps, FormCheckbox } from '../../../../components/FormCore'
import { GenericDialogLayout } from '../../../../components/GenericDialogLayout'
import { MainButton } from '../../../../components/MainButton'
import { IPositionType } from '../../../../generated-types'
import { IPositionDrawing } from '../../../../generated-types/position-drawing'
import { usePositionDrawingFilters } from '../../../../hooks/usePositionDrawingFilters'
import { useZodForm } from '../../../../hooks/zod-form'
import { PositionDrawingFilters } from '../../../admin/position-drawing/components/positionDrawingFilters'
import PositionDrawingNumberBox from './PositionDrawingNumberBox'
import {
  ImgBox,
  ImgContainer,
  ImgList,
  PositionDrawingThumbnailFrame,
  PreviewNavButton,
  SelectedDrawingHeader,
  SelectedDrawingSection,
  SelectedPositionDrawingPreview,
} from './SelectPositionDrawingForm.style'
import { GridActionButton } from 'src/components/common/GridActionButton'

export const positionDrawingSchema = z.object({
  positionDrawing: z.any(),
  replaceComponents: z.boolean().default(false),
})

interface SelectPositionDrawingFormProps extends AppFormProps {
  positionDrawingId: EntityId
  positionType: IPositionType
  setSelectedPositionDrawing?: React.Dispatch<React.SetStateAction<IPositionDrawing | undefined>>
  isUpdateForm: boolean | undefined
  customerId: EntityId
  mooringId?: EntityId
  selectedPositionDrawing?: IPositionDrawing
  useSelfSubmit?: boolean
  pop?: (value?: boolean) => void
  showReplaceComponents?: boolean
  onDrawingSelected?: (drawing: IPositionDrawing) => void
}

export const SelectPositionDrawingForm: React.FC<SelectPositionDrawingFormProps> = ({
  onSubmit,
  positionDrawingId,
  positionType,
  setSelectedPositionDrawing,
  isUpdateForm,
  customerId,
  mooringId,
  selectedPositionDrawing,
  useSelfSubmit,
  pop,
  showReplaceComponents = true,
  onDrawingSelected,
}) => {
  const { t } = useTranslation(['position', 'common'])
  const [numberOfElements, setNumberOfElements] = useState(1)
  const { filters, debouncedSearch, queryCategoryIds, onSearchChange, onProductCategoriesChange } =
    usePositionDrawingFilters()

  const { data: mooringData, refetch: refetchMooring } = useGetPositionDrawingsForMooring(
    mooringId,
    debouncedSearch,
    positionType,
    0,
    8 * numberOfElements,
    queryCategoryIds,
  )

  const {
    data: allData,
    refetch: refetchAll,
    isLoading: isLoadingAll,
  } = useGetDropDownPositionDrawings(
    debouncedSearch,
    0,
    APIResultCount.Max,
    positionType,
    customerId,
    queryCategoryIds,
    mooringId,
  )

  const data =
    mooringId != null
      ? mooringData
      : {
          items: allData ?? [],
          totalCount: allData?.length ?? 0,
        }
  const refetch = useCallback(() => {
    refetchMooring()
    refetchAll()
  }, [refetchMooring, refetchAll])

  const [updated, setUpdated] = useState(false)

  const form = useZodForm(positionDrawingSchema)
  const [selectedDrawing, setSelectedDrawing] = useState<IPositionDrawing | undefined>(undefined)
  const isSelectedDrawingAvailable = useMemo(() => {
    return data?.items?.find(d => d !== undefined && d.id == positionDrawingId)
  }, [data?.items, positionDrawingId])

  useEffect(() => {
    if (!isSelectedDrawingAvailable && selectedPositionDrawing && selectedPositionDrawing?.drawingNumber) {
      data?.items.push(selectedPositionDrawing as IPositionDrawing)
    }
    setSelectedDrawing(previous => {
      if (previous?.id) return previous
      return (
        data?.items?.find(d => d.id == positionDrawingId) ??
        (selectedPositionDrawing?.id ? selectedPositionDrawing : undefined)
      )
    })
  }, [data?.items, isSelectedDrawingAvailable, positionDrawingId, selectedPositionDrawing])

  useEffect(() => {
    form.setValue('positionDrawing', selectedDrawing)
  }, [form, selectedDrawing])

  useEffect(() => {
    if (mooringId != null) {
      document
        .getElementById(`drawing-${selectedDrawing?.id ?? positionDrawingId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [data?.items, positionDrawingId, selectedDrawing, mooringId])

  useEffect(() => {
    setNumberOfElements(1)
  }, [debouncedSearch, queryCategoryIds])

  useEffect(() => {
    refetch()
  }, [debouncedSearch, queryCategoryIds, numberOfElements, refetch])

  useEffect(() => {
    setSelectedPositionDrawing && updated && setSelectedPositionDrawing(selectedDrawing)
    if (pop && isUpdateForm && updated) {
      pop()
    }
  }, [selectedDrawing, updated])

  const positionDrawings: Array<IPositionDrawing> = useMemo(() => {
    if (
      !isSelectedDrawingAvailable &&
      selectedPositionDrawing &&
      selectedPositionDrawing?.drawingNumber &&
      data?.items &&
      data?.items?.length > 0
    ) {
      data?.items.push(selectedPositionDrawing as IPositionDrawing)
    }
    return Array.from(data?.items ?? [])
  }, [data?.items])

  const currentIndex = useMemo(() => {
    return positionDrawings.findIndex(x => x.id == selectedDrawing?.id) == -1
      ? 0
      : positionDrawings.findIndex(x => x.id == selectedDrawing?.id)
  }, [positionDrawings, selectedDrawing?.id])

  const handleSubmit = () => {
    const formData = form.getValues()
    const resolvedDrawing = (formData.positionDrawing ?? selectedDrawing) as IPositionDrawing | undefined

    if (onDrawingSelected && resolvedDrawing) {
      onDrawingSelected(resolvedDrawing)
      pop?.(true)
      return
    }

    if (useSelfSubmit) {
      onSubmit(formData)
    } else if (resolvedDrawing) {
      const stateData = resolvedDrawing
      stateData.regenerateComponents = formData.replaceComponents
      setSelectedPositionDrawing?.(stateData)
      setUpdated(true)
      pop?.(true)
    }
  }

  const handleRemoveDrawing = useCallback(() => {
    setSelectedDrawing(undefined)
  }, [])

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      const newPositionDrawing = positionDrawings.find((_, i) => i == currentIndex - 1)
      setSelectedDrawing(newPositionDrawing)
    }
  }, [currentIndex, positionDrawings])

  const handleForward = useCallback(() => {
    if (currentIndex < positionDrawings.length - 1) {
      if (selectedDrawing?.id == undefined) {
        setSelectedDrawing(positionDrawings[0])
      } else {
        const newPositionDrawing = positionDrawings.find((_, i) => i == currentIndex + 1)
        setSelectedDrawing(newPositionDrawing)
      }
    }
  }, [currentIndex, positionDrawings, selectedDrawing])

  return (
    <GenericDialogLayout
      title={
        positionDrawingId
          ? t('updatePositionDrawing', { ns: 'position' })
          : t('selectPositionDrawing', { ns: 'position' })
      }
      pop={pop}
      removedCloseButton={false}
      actions={[
        <MainButton
          disabled={showReplaceComponents ? positionDrawingId == selectedDrawing?.id : !selectedDrawing}
          key="add-position-drawing"
          onClick={() => {
            handleSubmit()
            setUpdated(true)
          }}
          loading={form.formState.isSubmitting}
        >
          {positionDrawingId ? t('update', { ns: 'common' }) : t('add', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form} onSubmit={onSubmit}>
        <Grid container p={1} spacing={1}>
          <Grid item xs={12}>
            <Box>
              <PositionDrawingFilters
                filters={filters}
                onSearchChange={onSearchChange}
                onProductCategoriesChange={onProductCategoriesChange}
                padding={0}
              />

              {form.formState.errors.positionDrawing && (
                <FormHelperText error={true} sx={{ textAlign: 'left' }}>
                  {form.formState.errors.positionDrawing.message}
                </FormHelperText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <ImgList
              scrollThreshold={0.9}
              dataLength={data?.items?.length ?? 0}
              height={180}
              next={() => {
                if ((data && data?.items?.length < data?.totalCount) ?? false) setNumberOfElements(numberOfElements + 1)
              }}
              hasMore={(data && data?.items?.length < data?.totalCount) ?? false}
              loader={undefined}
            >
              {positionDrawings.length == 0 && !isLoadingAll && (
                <Box sx={{ gridColumn: '1 / -1', width: '100%', height: '100%' }}>
                  <EmptyStatusCard
                    compact
                    imageWidth="70px"
                    height="100%"
                    headerFontSize="1rem"
                    contentFontSize="0.8rem"
                    imageBottomMargin="8px"
                    header={t('drawings-not-found-header', { ns: 'position' })}
                    content={t('drawings-not-found-content', { ns: 'position' })}
                  />
                </Box>
              )}
              {positionDrawings.map(positionDrawing => (
                <ImgContainer
                  key={positionDrawing.id}
                  id={'drawing-' + positionDrawing.id}
                  onClick={() => {
                    setSelectedDrawing(positionDrawing)
                  }}
                >
                  {isLoadingAll && <FlexSpinner />}
                  <PositionDrawingThumbnailFrame isSelected={positionDrawing.id == selectedDrawing?.id}>
                    <ImgBox
                      className={'rounded-lg'}
                      height={90}
                      sx={{
                        backgroundImage: `url(${positionDrawing.fileUrl})`,
                        cursor: 'pointer',
                      }}
                    />
                    {positionDrawing.id == selectedDrawing?.id && (
                      <CheckCircleRounded
                        color="primary"
                        sx={{ marginLeft: '45px', marginTop: '-105px', borderRadius: '20px' }}
                      />
                    )}
                  </PositionDrawingThumbnailFrame>
                  <Typography variant="caption">{positionDrawing.drawingNumber}</Typography>
                </ImgContainer>
              ))}
            </ImgList>
          </Grid>
          <Grid item xs={12}>
            <SelectedDrawingSection>
              <SelectedDrawingHeader>
                <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography variant="body1" color="text.secondary" noWrap>
                    {t('position-drawing', { ns: 'position' })}:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" noWrap>
                    {selectedDrawing?.drawingNumber ?? t('n/a', { ns: 'common' })}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                  {showReplaceComponents && (
                    <FormCheckbox name="replaceComponents" label={t('replaceComponents', { ns: 'position' })} />
                  )}
                  {selectedDrawing?.drawingNumber != undefined && (
                    <GridActionButton
                      width={'36px'}
                      height={'36px'}
                      onClick={handleRemoveDrawing}
                      buttonIcon={<MdDeleteOutline size={20} />}
                    />
                  )}
                </Stack>
              </SelectedDrawingHeader>
              <SelectedPositionDrawingPreview imageUrl={selectedDrawing?.fileUrl}>
                <PreviewNavButton className="prev" disabled={currentIndex === 0} onClick={handleBack} aria-label="back">
                  <MdArrowBack />
                </PreviewNavButton>
                {selectedDrawing ? (
                  <PositionDrawingNumberBox selectedDrawingNumber={selectedDrawing.drawingNumber ?? ''} />
                ) : (
                  <EmptyStatusCard
                    compact
                    imageWidth="70px"
                    height="100%"
                    headerFontSize="1rem"
                    contentFontSize="0.8rem"
                    imageBottomMargin="8px"
                    header={t('position-drawing-empty-status-title', { ns: 'position' })}
                    content={t('position-drawing-empty-status-content', { ns: 'position' })}
                  />
                )}
                <PreviewNavButton
                  className="next"
                  disabled={currentIndex === positionDrawings.length - 1}
                  onClick={handleForward}
                  aria-label="forward"
                >
                  <MdArrowForward />
                </PreviewNavButton>
              </SelectedPositionDrawingPreview>
            </SelectedDrawingSection>
          </Grid>
        </Grid>
      </AppForm>
    </GenericDialogLayout>
  )
}
